import { Dictionary } from 'lodash';
import { Team, User } from 'api/models';
import {
  AllocationMedium,
  AllocationsGroupByTeam,
  ResourceAllocations,
  ResourceType,
  TeamResourceSummary,
  UserResourceSummary,
} from 'api/resource-management/types';
import {
  CompanyAllocationSummaryEvent,
  RecourceSummaries,
  TeamAllocationSummaryEvent,
  UserAllocationSummaryEvent,
} from 'components/organisms/resource-management/event-types';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import { getTotalAllocatedMinutes, INVALID_ROLE_ID } from '../common';
import { companyAllocationSummaryAsEvent } from '../events-conversion/resource-summary/company-summary';
import { teamAllocationSummaryAsEvent } from '../events-conversion/resource-summary/team-summary';
import { userAllocationSummaryAsEvent } from '../events-conversion/resource-summary/user-summary';

export const getNonZeroSummaryEvents = <
  T extends TeamAllocationSummaryEvent | CompanyAllocationSummaryEvent,
>(
  summaries: T[],
): T[] =>
  summaries.filter((event) => {
    const minutes = getTotalAllocatedMinutes({
      [AllocationMedium.Hard]: event.allocatedMinutes[AllocationMedium.Hard],
      [AllocationMedium.Soft]: event.allocatedMinutes[AllocationMedium.Soft],
    });

    return minutes > 0;
  });

const getUserSummaries = (
  userLookup: Dictionary<User>,
  userId: number,
  summaries: UserResourceSummary[],
  currentViewPreset: ViewPresetId,
  softAllocationsEnabled?: boolean,
): UserAllocationSummaryEvent[] =>
  summaries.map(({ capacity, allocatedMinutes, ...rest }) =>
    userAllocationSummaryAsEvent(
      userLookup,
      {
        ...rest,
        userId,
        forViewPreset: currentViewPreset,
        capacity: capacity * 60,
        allocatedMinutes: {
          [AllocationMedium.Hard]: allocatedMinutes.hard,
          [AllocationMedium.Soft]: allocatedMinutes.soft,
        },
      },
      softAllocationsEnabled,
    ),
  );

const getTeamSummaries = (
  roleId: number,
  summaries: TeamResourceSummary[],
  currentViewPreset: ViewPresetId,
  softAllocationsEnabled?: boolean,
  companyId?: number,
): TeamAllocationSummaryEvent[] =>
  /**
   * To ignore zero minute summaries while consuming
   * Not doing the check here in order to handle socket updates
   * where old team summary events need to be removed
   * Can invoke getNonZeroTeamSummaryEvents() written here
   */
  summaries.map(({ allocatedMinutes, ...rest }) =>
    teamAllocationSummaryAsEvent(
      {
        ...rest,
        roleId,
        forViewPreset: currentViewPreset,
        allocatedMinutes: {
          [AllocationMedium.Hard]: allocatedMinutes.hard,
          [AllocationMedium.Soft]: allocatedMinutes.soft,
        },
      },
      softAllocationsEnabled,
      companyId,
    ),
  );
const getCompanySummaries = (
  companyId: number,
  summaries: TeamResourceSummary[],
  currentViewPreset: ViewPresetId,
  softAllocationsEnabled?: boolean,
): CompanyAllocationSummaryEvent[] =>
  /**
   * To ignore zero minute summaries while consuming
   * Not doing the check here in order to handle socket updates
   * where old team summary events need to be removed
   * Can invoke getNonZeroTeamSummaryEvents() written here
   */
  summaries.map(({ allocatedMinutes, ...rest }) =>
    companyAllocationSummaryAsEvent(
      companyId,
      {
        ...rest,
        roleId: INVALID_ROLE_ID,
        forViewPreset: currentViewPreset,
        allocatedMinutes: {
          [AllocationMedium.Hard]: allocatedMinutes.hard,
          [AllocationMedium.Soft]: allocatedMinutes.soft,
        },
      },
      softAllocationsEnabled,
    ),
  );
const getResolvedCompanyIdsForTeam = (
  resource: AllocationsGroupByTeam,
  teamMembership: Record<string, Team[]>,
) => {
  const projectIdsInWhichTeamsExists = resource.aggregations.reduce(
    (result, { projectId }) => result.concat(projectId),
    [] as number[],
  );

  return projectIdsInWhichTeamsExists.reduce((result, projectId) => {
    const resolvedWith = teamMembership[projectId]?.find(
      (team) => team.teamId === resource.team.teamId,
    )?.companyList?.[0]?.companyId;

    return resolvedWith != null ? result.concat(resolvedWith) : result;
  }, [] as number[]);
};

export const getResourceSummaries = (
  userLookup: Dictionary<User>,
  teamMembership: Record<string, Team[]>,
  resourceAllocations: ResourceAllocations,
  currentViewPreset: ViewPresetId,
  softAllocationsEnabled?: boolean,
): RecourceSummaries => {
  const userSummaries: Dictionary<UserAllocationSummaryEvent[]> = {};
  const teamSummaries: Dictionary<TeamAllocationSummaryEvent[]> = {};
  const companySummaries: Dictionary<CompanyAllocationSummaryEvent[]> = {};

  resourceAllocations.forEach((resource) => {
    if (resource.type === ResourceType.User) {
      const {
        user: {
          userId,
          company: { companyId },
        },
      } = resource;

      if (userSummaries[userId] == null) {
        userSummaries[userId] = getUserSummaries(
          userLookup,
          userId,
          resource.summaries ?? [],
          currentViewPreset,
          softAllocationsEnabled,
        );
      }
      if (companySummaries[companyId] == null) {
        companySummaries[companyId] = getCompanySummaries(
          companyId,
          resource.companySummaries ?? [],
          currentViewPreset,
          softAllocationsEnabled,
        );
      }
    } else {
      const resolvedWithCompanyIds = getResolvedCompanyIdsForTeam(
        resource,
        teamMembership,
      );

      if (resolvedWithCompanyIds.length === 0) {
        if (teamSummaries[resource.team.roleId ?? INVALID_ROLE_ID] == null) {
          teamSummaries[resource.team.roleId ?? INVALID_ROLE_ID] =
            getTeamSummaries(
              resource.team.roleId ?? INVALID_ROLE_ID,
              resource.summaries ?? [],
              currentViewPreset,
              softAllocationsEnabled,
            );
        }
      } else {
        resolvedWithCompanyIds.forEach((companyId) => {
          if (
            teamSummaries[
              `${resource.team.roleId ?? INVALID_ROLE_ID}_${companyId}`
            ] == null
          ) {
            teamSummaries[
              `${resource.team.roleId ?? INVALID_ROLE_ID}_${companyId}`
            ] = getTeamSummaries(
              resource.team.roleId ?? INVALID_ROLE_ID,
              resource.summaries ?? [],
              currentViewPreset,
              softAllocationsEnabled,
              companyId,
            );
          }
          if (companySummaries[companyId] == null) {
            companySummaries[companyId] = getCompanySummaries(
              companyId,
              resource.companySummaries ?? [],
              currentViewPreset,
              softAllocationsEnabled,
            );
          }
        });
      }
    }
  });

  return {
    userSummaries,
    teamSummaries,
    companySummaries,
  };
};
